import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';
import { Client } from 'src/providers/client/client';

import { Loading } from 'src/shared/components/loading/loading';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';

import { AddressUpgradesService } from 'src/shared/services/address-upgrades.service';
import { AddressUpgrade, UPGRADE_IMPACT_OPTIONS, UPGRADE_STATUS_OPTIONS } from 'src/models/address-upgrade.model';

@Component({
  templateUrl: 'add-edit-address-upgrade.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AddEditAddressUpgradePage implements OnInit {

  private fb = inject(UntypedFormBuilder);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private modalController = inject(ModalController);
  private util = inject(Util);
  private client = inject(Client);
  private addressUpgradesService = inject(AddressUpgradesService);

  form: UntypedFormGroup;
  submitted = false;
  loaded = false;
  isRightSideContent = true;
  errorMessage = '';
  dialogParams: any;
  propertyItems: { value: number; viewValue: string }[] = [];
  statusItems = UPGRADE_STATUS_OPTIONS;
  impactItems = UPGRADE_IMPACT_OPTIONS;
  isEditMode = false;
  editingUpgrade: AddressUpgrade | null = null;

  boundSave = this.save.bind(this);
  boundDelete = this.deleteUpgrade.bind(this);

  constructor() {
    this.form = this.fb.group({
      property_id: ['', Validators.required],
      name: ['', Validators.required],
      description: [''],
      estimated_cost: [''],
      impact_score: [''],
      roi_score: [''],
      status: ['not_done'],
    });
  }

  async ngOnInit() {
    try {
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      this.dialogParams = await this.storage.retrieve('dialog-params');

      this.isEditMode = !!this.dialogParams?.upgrade;
      this.editingUpgrade = this.dialogParams?.upgrade || null;

      this.rightSidePanelService.setDialogPageTitle(this.isEditMode ? 'Edit Upgrade' : 'Add Upgrade');

      await this.loadAddresses();

      if (this.isEditMode && this.editingUpgrade) {
        this.form.patchValue({
          property_id: this.editingUpgrade.address?.id,
          name: this.editingUpgrade.name,
          description: this.editingUpgrade.description || '',
          // estimated_cost is stored in cents on the server; show whole dollars in the form
          estimated_cost: this.editingUpgrade.estimated_cost !== null && this.editingUpgrade.estimated_cost !== undefined
            ? this.editingUpgrade.estimated_cost / 100
            : '',
          impact_score: this.editingUpgrade.impact_score ?? '',
          roi_score: this.editingUpgrade.roi_score ?? '',
          status: this.editingUpgrade.status || 'not_done',
        });
      } else if (this.dialogParams?.addressId) {
        this.form.patchValue({ property_id: this.dialogParams.addressId });
      }

      this.loaded = true;
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load page';
    }
  }

  async loadAddresses() {
    const addresses = await this.client.getAddresses();
    this.propertyItems = addresses.map(address => ({
      value: address.id,
      viewValue: address.address_name || `${address.address1}${address.address2 ? ` ${address.address2}` : ''}, ${address.city}, ${address.zip}`,
    }));
  }

  @Loading('', true)
  async save() {
    this.errorMessage = '';
    this.submitted = true;
    if (!this.form.valid) {
      return;
    }
    try {
      const payload = this.buildPayload();
      if (this.isEditMode && this.editingUpgrade) {
        await this.addressUpgradesService.updateAddressUpgrade(this.editingUpgrade.id, payload);
      } else {
        await this.addressUpgradesService.createAddressUpgrade(payload);
      }
      await this.storage.save('didMakeAddressUpgradeChanges', true);
      this.util.showSuccess(this.isEditMode ? 'Upgrade updated successfully.' : 'Upgrade added successfully.');
      this.goBack();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to save upgrade';
    }
  }

  async deleteUpgrade() {
    const confirmParams: ConfirmPageParamsModel = {
      title: 'Delete Upgrade?',
      body: 'Are you sure you want to delete this upgrade?',
      backText: 'Go Back',
      confirmText: 'Delete Upgrade',
      confirmAction: this.confirmDeleteUpgrade.bind(this),
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: confirmParams,
      animated: false,
      cssClass: 'confirm-modal',
    });
    confirmationModal.present();
  }

  async confirmDeleteUpgrade() {
    try {
      await this.addressUpgradesService.deleteAddressUpgrade(this.editingUpgrade.id);
      await this.storage.save('didMakeAddressUpgradeChanges', true);
      this.modalController.dismiss();
      this.util.showSuccess('Upgrade deleted successfully.');
      this.goBack();
    } catch (err) {
      this.modalController.dismiss();
      this.errorMessage = err?.error?.message || err?.message || 'Failed to delete upgrade';
    }
  }

  private buildPayload(): Record<string, any> {
    const val = this.form.value;
    const payload: any = {
      address_id: val.property_id,
      name: val.name,
      status: val.status || 'not_done',
    };
    if (val.description) { payload.description = val.description; }
    if (val.estimated_cost !== '' && val.estimated_cost !== null) {
      payload.estimated_cost = Math.round(Number(val.estimated_cost) * 100);
    }
    if (val.impact_score !== '' && val.impact_score !== null) {
      payload.impact_score = Number(val.impact_score);
    }
    if (val.roi_score !== '' && val.roi_score !== null) {
      payload.roi_score = Number(val.roi_score);
    }
    return payload;
  }

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.back();
    }
  }
}
